import React from 'react';
import styled from 'styled-components';
import { useData } from '../../contexts/DataContext';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.5rem;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin-top: 0.75rem;
    display: none;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 0.5rem;
  text-align: center;
  margin-bottom: 0;

  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 0.5rem;
  }
`;

const StatNumber = styled.div`
  color: ${props => props.color || props.theme.text};
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.125rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const StatLabel = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const QuickStats = () => {
  const { assignments = [], courses = [] } = useData();
  
  // Create a map of course IDs to course status for quick lookup
  const courseStatusMap = (courses || []).reduce((map, course) => {
    map[course._id || course.id] = course.status;
    return map;
  }, {});
  
  // Filter out assignments from completed courses
  const activeAssignments = (assignments || []).filter(assignment => {
    const courseId = assignment.course?._id || assignment.course?.id || assignment.course;
    return courseStatusMap[courseId] !== 'completed';
  });
  
  const totalAssignments = activeAssignments.length;
  const completedAssignments = activeAssignments.filter(a => a.status === 'completed').length;
  const pendingAssignments = activeAssignments.filter(a => a.status === 'pending').length;
  const inProgressAssignments = activeAssignments.filter(a => a.status === 'in-progress').length;
  const activeCourses = (courses || []).filter(c => c.status !== 'completed').length;
  const completionRate = totalAssignments > 0 ? Math.round((completedAssignments / totalAssignments) * 100) : 0;

  return (
    <StatsGrid>
      <StatCard borderColor="#5865f2">
        <StatNumber color="#5865f2">{totalAssignments}</StatNumber>
        <StatLabel>Active Assignments</StatLabel>
      </StatCard>
      <StatCard borderColor="#9c27b0">
        <StatNumber color="#9c27b0">{activeCourses}</StatNumber>
        <StatLabel>Active Courses</StatLabel>
      </StatCard>
      <StatCard borderColor="#28a745">
        <StatNumber color="#28a745">{completedAssignments}</StatNumber>
        <StatLabel>Completed</StatLabel>
      </StatCard>
      <StatCard borderColor="#fbc02d">
        <StatNumber color="#fbc02d">{inProgressAssignments}</StatNumber>
        <StatLabel>In Progress</StatLabel>
      </StatCard>
      <StatCard borderColor="#dc3545">
        <StatNumber color="#dc3545">{pendingAssignments}</StatNumber>
        <StatLabel>Pending</StatLabel>
      </StatCard>
      <StatCard borderColor="#2196f3">
        <StatNumber color="#2196f3">{completionRate}%</StatNumber>
        <StatLabel>Completion Rate</StatLabel>
      </StatCard>
    </StatsGrid>
  );
};

export default QuickStats;
